import { useState } from 'react';
import type { Group } from '@/types';

interface BulkEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (changes: {
    group_id: string | null;
    addTags: string[];
    removeTags: string[];
    replaceTags: string[] | null;
    state: string | null;
  }) => void;
  groups: Group[];
  saving?: boolean;
  selectedCount: number;
}

export default function BulkEditModal({ isOpen, onClose, onConfirm, groups, saving, selectedCount }: BulkEditModalProps) {
  const [groupId, setGroupId] = useState('');
  const [tagMode, setTagMode] = useState<'none' | 'add' | 'remove' | 'replace'>('none');
  const [tagInput, setTagInput] = useState('');
  const [state, setState] = useState('');

  if (!isOpen) return null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const addTags = tagMode === 'add'
      ? tagInput.split(',').map((t) => t.trim()).filter(Boolean)
      : [];
    const removeTags = tagMode === 'remove'
      ? tagInput.split(',').map((t) => t.trim()).filter(Boolean)
      : [];
    const replaceTags = tagMode === 'replace'
      ? tagInput.split(',').map((t) => t.trim()).filter(Boolean)
      : null;

    onConfirm({
      group_id: groupId || null,
      addTags,
      removeTags,
      replaceTags,
      state: state || null,
    });
  };

  const hasChanges = groupId || tagMode !== 'none' || state;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-xl border border-background-200 w-full max-w-[480px] mx-4 animate-fadeIn">
        <div className="flex items-center justify-between px-5 py-4 border-b border-background-100">
          <h3 className="text-base font-heading font-semibold text-foreground-950">Editar em lote</h3>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg text-foreground-400 hover:bg-background-100 hover:text-foreground-600 transition-colors cursor-pointer">
            <i className="ri-close-line text-xl" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-5 space-y-5">
          {/* Selected count */}
          <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-primary-50 border border-primary-100">
            <i className="ri-user-line text-primary-600 text-sm" />
            <p className="text-sm text-primary-700 font-medium">
              {selectedCount} contato{selectedCount > 1 ? 's' : ''} selecionado{selectedCount > 1 ? 's' : ''}
            </p>
          </div>

          {/* Group */}
          <div>
            <label className="block text-sm font-medium text-foreground-700 mb-1.5">Alterar grupo</label>
            <select
              value={groupId}
              onChange={(e) => setGroupId(e.target.value)}
              className="w-full px-3 py-2.5 bg-white border border-background-200 rounded-lg text-sm text-foreground-900 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
            >
              <option value="">Manter grupo atual</option>
              <option value="__none__">Remover grupo (sem grupo)</option>
              {groups.map((g) => (
                <option key={g.id} value={g.id}>{g.name}</option>
              ))}
            </select>
            <p className="text-xs text-foreground-400 mt-1">Escolha um grupo ou deixe como está.</p>
          </div>

          {/* Tags */}
          <div>
            <label className="block text-sm font-medium text-foreground-700 mb-1.5">Gerenciar tags</label>
            <div className="flex gap-2 mb-2">
              {([
                { key: 'none', label: 'Não alterar' },
                { key: 'add', label: 'Adicionar' },
                { key: 'remove', label: 'Remover' },
                { key: 'replace', label: 'Substituir' },
              ] as const).map((m) => (
                <button
                  key={m.key}
                  type="button"
                  onClick={() => { setTagMode(m.key); setTagInput(''); }}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors whitespace-nowrap cursor-pointer ${
                    tagMode === m.key
                      ? 'bg-primary-100 text-primary-700 border border-primary-200'
                      : 'bg-white text-foreground-500 border border-background-200 hover:bg-background-50'
                  }`}
                >
                  {m.label}
                </button>
              ))}
            </div>
            {tagMode !== 'none' && (
              <input
                type="text"
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                placeholder={
                  tagMode === 'add' ? 'Adicionar tags: vip, promoção, cliente...'
                    : tagMode === 'remove' ? 'Remover tags: lead, frio...'
                      : 'Substituir por: vip, ativo...'
                }
                className="w-full px-3 py-2.5 bg-white border border-background-200 rounded-lg text-sm text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
              />
            )}
          </div>

          {/* State */}
          <div>
            <label className="block text-sm font-medium text-foreground-700 mb-1.5">Alterar estado (UF)</label>
            <select
              value={state}
              onChange={(e) => setState(e.target.value)}
              className="w-full px-3 py-2.5 bg-white border border-background-200 rounded-lg text-sm text-foreground-900 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
            >
              <option value="">Manter estado atual</option>
              {['AC','AL','AP','AM','BA','CE','DF','ES','GO','MA','MT','MS','MG','PA','PB','PR','PE','PI','RJ','RN','RS','RO','RR','SC','SP','SE','TO'].map((uf) => (
                <option key={uf} value={uf}>{uf}</option>
              ))}
            </select>
          </div>

          <div className="flex items-center justify-end gap-3 pt-2 border-t border-background-100">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-foreground-600 hover:bg-background-100 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={saving || !hasChanges}
              className="px-5 py-2 bg-primary-500 hover:bg-primary-600 disabled:bg-primary-300 disabled:cursor-not-allowed text-white text-sm font-semibold rounded-lg transition-colors whitespace-nowrap cursor-pointer flex items-center gap-2"
            >
              {saving ? (
                <>
                  <i className="ri-loader-4-line animate-spin" />
                  Salvando...
                </>
              ) : (
                <>
                  <i className="ri-save-line" />
                  Salvar alterações em {selectedCount} contato{selectedCount > 1 ? 's' : ''}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}